import { useState, useEffect } from "react";
import {
  CogIcon,
  PlusIcon,
  XMarkIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { trpc } from "../../lib/api/trpc";
import { usePermissions } from "../../hooks/usePermissions";

type ExtractorConfig = {
  focusAreas?: string[];
  customInstructions?: string;
  documentDomain?: string;
  preserveFormatting?: boolean;
};

export function ExtractionSettingsPage() {
  const { isAdmin, isOwner } = usePermissions();
  const canEdit = isAdmin || isOwner;

  // @ts-ignore - tRPC type issues
  const { data, isLoading, refetch } =
    trpc.organization.getExtractionStrategy.useQuery();

  // @ts-ignore - tRPC type issues
  const updateMutation = trpc.organization.updateExtractionConfig.useMutation({
    onSuccess: () => {
      refetch();
      setSaveMessage("Settings saved successfully");
      setTimeout(() => setSaveMessage(""), 3000);
    },
  });

  // @ts-ignore - tRPC type issues
  const resetMutation = trpc.organization.resetExtractionStrategy.useMutation({
    onSuccess: () => {
      refetch();
      setSaveMessage("Reset to system defaults");
      setTimeout(() => setSaveMessage(""), 3000);
    },
  });

  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [newFocusArea, setNewFocusArea] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [saveMessage, setSaveMessage] = useState("");

  // Initialize form state from data
  useEffect(() => {
    if (data?.activeStrategy) {
      const config =
        (data.activeStrategy.extractorConfig as ExtractorConfig) || {};
      setFocusAreas(config.focusAreas || []);
      setCustomInstructions(config.customInstructions || "");
    } else if (data?.systemDefaults?.[0]) {
      const config =
        (data.systemDefaults[0].extractorConfig as ExtractorConfig) || {};
      setFocusAreas(config.focusAreas || []);
      setCustomInstructions(config.customInstructions || "");
    }
  }, [data]);

  const handleAddFocusArea = () => {
    const trimmed = newFocusArea.trim().toLowerCase().replace(/\s+/g, "_");
    if (trimmed && !focusAreas.includes(trimmed)) {
      setFocusAreas([...focusAreas, trimmed]);
      setNewFocusArea("");
    }
  };

  const handleRemoveFocusArea = (area: string) => {
    setFocusAreas(focusAreas.filter((a) => a !== area));
  };

  const handleSave = () => {
    updateMutation.mutate({
      focusAreas,
      customInstructions: customInstructions || undefined,
    });
  };

  const handleReset = () => {
    if (
      window.confirm(
        "Reset to system defaults? Your custom settings will be removed.",
      )
    ) {
      resetMutation.mutate();
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    );
  }

  const activeStrategy = data?.activeStrategy;
  const isCustom = data?.isUsingCustom;
  const strategyName = activeStrategy?.name || "System Default";
  const strategyDesc =
    activeStrategy?.description ||
    data?.systemDefaults?.[0]?.description ||
    "Using the platform default extraction settings";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          Extraction Settings
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure how documents are processed and extracted
        </p>
      </div>

      {/* Active Strategy */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <CogIcon className="w-5 h-5 text-gray-500" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Active Strategy
            </h3>
          </div>
          <span
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isCustom
                ? "bg-accent-tint text-accent"
                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {isCustom ? "Custom" : "System Default"}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">
          {strategyName}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {strategyDesc}
        </p>
        {activeStrategy && (
          <div className="mt-3 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
            <span>Parser: {activeStrategy.parserType}</span>
            <span>Chunking: {activeStrategy.chunkingType}</span>
            <span>Version: {activeStrategy.version}</span>
          </div>
        )}
      </div>

      {/* Focus Areas */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Focus Areas
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Content types to emphasise during AI extraction
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {focusAreas.map((area) => (
            <span
              key={area}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent-tint text-accent text-sm"
            >
              {area}
              {canEdit && (
                <button
                  onClick={() => handleRemoveFocusArea(area)}
                  className="hover:text-accent-strong"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
          {focusAreas.length === 0 && (
            <span className="text-sm text-gray-400">
              No focus areas configured
            </span>
          )}
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <input
              type="text"
              value={newFocusArea}
              onChange={(e) => setNewFocusArea(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddFocusArea()}
              placeholder="Add focus area (e.g. specifications, warnings)"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={handleAddFocusArea}
              className="px-3 py-2 bg-accent text-white rounded-lg text-sm hover:bg-accent-strong transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Custom Instructions */}
      <div className="bg-white dark:bg-darkmode-800 rounded-lg shadow-box p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Custom Instructions
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          These instructions are appended to the AI extraction prompt. Use them
          to guide how your documents are processed.
        </p>
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          disabled={!canEdit}
          maxLength={1000}
          rows={4}
          placeholder="e.g. Preserve exact temperatures and durations. Keep safety warnings as distinct sections."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-darkmode-700 text-gray-900 dark:text-white disabled:opacity-50"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">
          {customInstructions.length}/1000
        </p>
      </div>

      {/* Actions */}
      {canEdit && (
        <div className="flex items-center justify-between">
          <div>
            {isCustom && (
              <button
                onClick={handleReset}
                disabled={resetMutation.isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-darkmode-700 transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Reset to Default
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {saveMessage && (
              <span className="text-sm text-green-600 dark:text-green-400">
                {saveMessage}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-strong disabled:opacity-50 transition-colors"
            >
              {updateMutation.isLoading ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
